/**
 * PLANILLA GENERAL DE INSCRIPCIÓN — el formato en papel de la Asociación,
 * armado con los datos que ya están en la plataforma.
 *
 * Hasta ahora el director llenaba la planilla a mano (o en un Excel suelto)
 * copiando nombres, documentos y edades de la plataforma. Todo eso ya está
 * inscrito: lo único que la planilla pide y la plataforma no sabía era si el
 * aventurero asiste CON uniforme (separa la sección D de la G) y tres rótulos
 * del encabezado — zona, pastor asistente y ministro.
 *
 * La planilla se arma SOLO con lo que hay: no inventa filas en blanco ni
 * asume cargos. Si una categoría del evento no encaja en ninguna sección, cae
 * en "OTROS" — nunca se pierde a alguien por un rótulo que no reconocemos.
 */
import {
  BLOOD_TYPE_LABELS,
  GENDER_LABELS,
  type Club,
  type Event,
  type EventRegistration,
  type Person,
} from "@/types";

// ============================================================
// SECCIONES
// ============================================================

export type PlanillaSectionKey =
  | "directiva"
  | "cocina"
  | "aventureros_uniforme"
  | "consejeros"
  | "acompanantes"
  | "amigos"
  | "otros";

/** Orden y rótulos tal como salen en la planilla impresa. */
const SECTIONS: {
  key: PlanillaSectionKey;
  code: string;
  title: string;
  /** Las secciones de niños llevan la columna "Bautizado"; las de adultos no. */
  showBaptized: boolean;
}[] = [
  { key: "directiva", code: "B", title: "DIRECTIVA DEL CLUB", showBaptized: false },
  { key: "cocina", code: "C", title: "PERSONAL DE COCINA", showBaptized: false },
  {
    key: "aventureros_uniforme",
    code: "D",
    title: "AVENTUREROS CON UNIFORME",
    showBaptized: true,
  },
  { key: "consejeros", code: "E", title: "CONSEJEROS", showBaptized: false },
  {
    key: "acompanantes",
    code: "F",
    title: "PADRES / ACOMPAÑANTES",
    showBaptized: false,
  },
  {
    key: "amigos",
    code: "G",
    title: "AVENTUREROS AMIGOS (SIN UNIFORME)",
    showBaptized: true,
  },
  { key: "otros", code: "H", title: "OTROS INSCRITOS", showBaptized: false },
];

/**
 * ¿La categoría corresponde a un participante (niño), que es a quien la
 * planilla separa por uniforme?
 *
 * Se decide por patrón y no por una lista cerrada porque las categorías las
 * escribe el admin al crear el evento: en el campamento de Aventureros 2026
 * son `aventurero_bautizado` / `aventurero_no_bautizado` /
 * `aventurero_hijo_pastor`, pero eventos viejos traen `pathfinder` y el
 * próximo camporee traerá otras.
 */
export function isParticipantCategory(category: string): boolean {
  const c = (category || "").toLowerCase();
  return (
    /^(aventurero|conquistador|guia_mayor|guias_mayores|acampante)/.test(c) ||
    c === "pathfinder"
  );
}

function sectionForCategory(
  category: string,
  hasUniform: boolean,
): PlanillaSectionKey {
  const c = (category || "").toLowerCase();
  if (isParticipantCategory(c)) {
    return hasUniform ? "aventureros_uniforme" : "amigos";
  }
  // El orden importa: `anciano_consejero` y `consejero_hijo_pastor` tienen que
  // caer en consejeros, no en la directiva.
  if (/consejero/.test(c) || c === "staff") return "consejeros";
  if (/^(director|subdirector|secretari|tesorer|capellan|instructor)/.test(c)) {
    return "directiva";
  }
  if (/econom|cocina|cociner/.test(c)) return "cocina";
  if (/acompanante|acompañante|padre|madre|familiar/.test(c)) {
    return "acompanantes";
  }
  if (c === "family" || c === "guest") return "acompanantes";
  return "otros";
}

/**
 * Bautizado según la categoría con la que se inscribió. En el campamento de
 * Aventureros el dato ya venía separado en dos categorías de precio, así que
 * no hubo que preguntarlo de nuevo.
 *
 * Ojo con el orden: "no_bautizado" CONTIENE "bautizado".
 */
export function baptizedFromCategory(category: string): boolean | null {
  const c = (category || "").toLowerCase();
  if (/no[_\s-]?bautizad/.test(c)) return false;
  if (/bautizad/.test(c)) return true;
  return null;
}

// ============================================================
// FILAS
// ============================================================

export interface PlanillaRow {
  order: number;
  fullName: string;
  documentType: string;
  documentNumber: string;
  birthDate: string;
  age: number | "";
  gender: string;
  bloodType: string;
  healthInsurance: string;
  phone: string;
  role: string;
  /** "Sí" | "No" | "" — sólo se imprime en las secciones de niños. */
  baptized: string;
}

export interface PlanillaSection {
  key: PlanillaSectionKey;
  code: string;
  title: string;
  showBaptized: boolean;
  rows: PlanillaRow[];
}

export interface PlanillaHeader {
  eventName: string;
  eventDates: string;
  clubName: string;
  churchName: string;
  districtName: string;
  zone: string;
  director: string;
  assistantPastor: string;
  minister: string;
  contactPhone: string;
  contactEmail: string;
  generatedAt: string;
}

export interface PlanillaData {
  header: PlanillaHeader;
  sections: PlanillaSection[];
  totals: {
    /** Niños con uniforme + amigos sin uniforme. */
    participants: number;
    /** Directiva + cocina + consejeros + acompañantes + otros. */
    adults: number;
    total: number;
    /** Lo que suman las inscripciones incluidas, a precio congelado. */
    amount: number;
  };
}

// ============================================================
// FECHAS
// ============================================================

/** Las lecturas pasan por convertTimestamps(), así que casi siempre es Date. */
function toDate(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === "object") {
    const v = value as { toDate?: () => Date; seconds?: number };
    if (typeof v.toDate === "function") {
      try {
        return v.toDate();
      } catch {
        return null;
      }
    }
    if (typeof v.seconds === "number") return new Date(v.seconds * 1000);
  }
  if (typeof value === "string" || typeof value === "number") {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

const fmtDate = (d: Date | null): string =>
  d ? d.toLocaleDateString("es-CO", { timeZone: "America/Bogota" }) : "";

/**
 * Edad el día que arranca el evento — igual que el filtro de inscripción.
 * Si se calculara "hoy", la planilla que se imprime en octubre le sumaría un
 * año a quien cumple entre la inscripción y el campamento, y dejaría de
 * cuadrar con el cupo por el que lo aceptamos.
 */
function ageAt(birthDate: Date | null, reference: Date): number | "" {
  if (!birthDate) return "";
  let age = reference.getFullYear() - birthDate.getFullYear();
  const m = reference.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && reference.getDate() < birthDate.getDate())) age -= 1;
  return age >= 0 && age < 120 ? age : "";
}

function eventDateRange(event: Event | null | undefined): string {
  const start = toDate(event?.startDate);
  const end = toDate(event?.endDate);
  if (!start) return "";
  if (!end || start.getTime() === end.getTime()) return fmtDate(start);
  return `${fmtDate(start)} — ${fmtDate(end)}`;
}

// ============================================================
// ARMADO
// ============================================================

/**
 * Quién sale como "Director(a)" en el encabezado — y, por tanto, quién firma
 * abajo: la persona que está generando la planilla.
 *
 * `clubs.directorName` NO sirve: es el director GENERAL del club, y en los
 * clubes con varias secciones no es quien arma la planilla de cada una. Caso
 * real de VICTORIA el 2026-10-02: el club tiene a Diego como director general
 * y a Nadian Stefany Torres Barboza como directora de Aventureros; la
 * planilla del campamento la hace Stefany y le salía el nombre de Diego, que
 * ni la llenó ni la va a firmar.
 *
 * El orden busca de lo más específico a lo más general, para que un director
 * general que genere la planilla de su club siga saliendo él.
 */
export function signerName(
  club: Club | null | undefined,
  generatedBy?: { userId?: string; displayName?: string },
): string {
  const uid = generatedBy?.userId;
  if (uid && club?.sections) {
    const seccion = Object.values(club.sections).find(
      (s) => s?.directorUserId === uid && s?.directorName,
    );
    if (seccion?.directorName) return seccion.directorName;
  }
  if (uid && club?.directorId === uid && club?.directorName) {
    return club.directorName;
  }
  return generatedBy?.displayName || club?.directorName || "";
}

export interface BuildPlanillaParams {
  event: Event | null | undefined;
  club: Club | null | undefined;
  /** Inscripciones del club en el evento (se filtran las muertas aquí). */
  registrations: EventRegistration[];
  /** Personas por id — `buildPersonsMap(usePersons().data)`. */
  personsById: Map<string, Person>;
  /** Nombre del distrito; se resuelve desde la iglesia del club. */
  districtName?: string;
  /** Lo que el director escribió en el modal; manda sobre lo guardado. */
  overrides?: {
    zone?: string;
    assistantPastor?: string;
    minister?: string;
    contactPhone?: string;
  };
  /** Quién la está generando: es quien sale como director y quien firma. */
  generatedBy?: { userId?: string; displayName?: string };
}

/** Sólo entran a la planilla las inscripciones vivas. */
export function isPlanillaRegistration(reg: EventRegistration): boolean {
  return reg.status === "approved" || reg.status === "pending";
}

export function buildPlanilla({
  event,
  club,
  registrations,
  personsById,
  districtName,
  overrides,
  generatedBy,
}: BuildPlanillaParams): PlanillaData {
  const reference = toDate(event?.startDate) ?? new Date();
  const categoryLabel = new Map(
    (event?.pricing ?? []).map((p) => [p.category, p.label]),
  );

  const buckets = new Map<PlanillaSectionKey, PlanillaRow[]>();
  for (const s of SECTIONS) buckets.set(s.key, []);

  const live = registrations
    .filter(isPlanillaRegistration)
    .sort((a, b) =>
      (a.personName || "").localeCompare(b.personName || "", "es", {
        sensitivity: "base",
      }),
    );

  let amount = 0;
  for (const reg of live) {
    amount += reg.unitPrice || 0;
    const person = personsById.get(reg.personId);
    const birth = toDate(person?.birthDate);
    const key = sectionForCategory(reg.category, reg.hasUniform === true);
    const baptized = baptizedFromCategory(reg.category);
    const bucket = buckets.get(key)!;
    bucket.push({
      order: bucket.length + 1,
      fullName: person?.fullName || reg.personName || "",
      documentType: person?.idDocumentType ?? "",
      documentNumber: person?.idDocumentNumber ?? "",
      birthDate: fmtDate(birth),
      age: ageAt(birth, reference),
      gender: person?.gender ? (GENDER_LABELS[person.gender] ?? "") : "",
      bloodType: person?.bloodType
        ? (BLOOD_TYPE_LABELS[person.bloodType] ?? "")
        : "",
      healthInsurance: person?.healthInsurance ?? "",
      phone: person?.phone || person?.emergencyContact?.phone || "",
      role: categoryLabel.get(reg.category) ?? reg.category,
      baptized: baptized === null ? "" : baptized ? "Sí" : "No",
    });
  }

  // Secciones vacías fuera: la planilla en papel trae renglones en blanco
  // porque se llena a mano; impresa desde la plataforma, un bloque vacío sólo
  // gasta tinta y hace dudar de si faltó cargar gente.
  const sections: PlanillaSection[] = SECTIONS.filter(
    (s) => (buckets.get(s.key)?.length ?? 0) > 0,
  ).map((s) => ({ ...s, rows: buckets.get(s.key)! }));

  const count = (k: PlanillaSectionKey) => buckets.get(k)?.length ?? 0;
  const participants = count("aventureros_uniforme") + count("amigos");
  const adults =
    count("directiva") +
    count("cocina") +
    count("consejeros") +
    count("acompanantes") +
    count("otros");

  return {
    header: {
      eventName: event?.name ?? "",
      eventDates: eventDateRange(event),
      clubName: club?.name ?? "",
      churchName: club?.churchName ?? "",
      districtName: districtName ?? "",
      zone: overrides?.zone ?? club?.zone ?? "",
      director: signerName(club, generatedBy),
      assistantPastor: overrides?.assistantPastor ?? club?.assistantPastor ?? "",
      minister: overrides?.minister ?? club?.minister ?? "",
      contactPhone: overrides?.contactPhone ?? club?.contactPhone ?? "",
      contactEmail: club?.contactEmail ?? "",
      generatedAt: new Date().toLocaleString("es-CO", {
        timeZone: "America/Bogota",
      }),
    },
    sections,
    totals: { participants, adults, total: participants + adults, amount },
  };
}

/** Nombre de archivo sin tildes ni espacios, para Excel y PDF. */
export function planillaFilename(clubName: string, eventName: string): string {
  const slug = (s: string) =>
    (s || "")
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .replace(/[^\w\s-]/g, "")
      .trim()
      .replace(/\s+/g, "_");
  const stamp = new Date().toISOString().slice(0, 10);
  return `planilla_${slug(clubName) || "club"}_${slug(eventName) || "evento"}_${stamp}`;
}
